import api from '../axios.config';

/**
 * The agent's own console — their seat, their queues, their availability.
 *
 * Everything is scoped server-side to the authenticated caller. There is no
 * userId to pass: changing another agent's availability is a supervisor action
 * and lives elsewhere, so the parameter does not exist to be misused.
 */
export interface QueueMember {
  name: string;
  interface: string;
  state: string;
  paused: boolean;
  pauseReason: string | null;
}

export interface QueueView {
  queue: string;
  waiting: number;
  strategy: string;
  members: QueueMember[];
  /** The worst caller waiting RIGHT NOW, not an average over finished calls. */
  longestWaitSeconds: number;
}

export interface MySeat {
  extension: string;
  displayName: string;
  followMeEnabled: boolean;
  /** How this seat is named inside the queue — a Local channel when follow-me. */
  memberInterface: string;
}

export interface MyState {
  extension: string;
  memberInterface: string;
  paused: boolean;
  pauseReason: string | null;
  /**
   * Device state from the switch. ⚠️ Being un-paused is NOT being reachable: an
   * unregistered phone reads "Unavailable" and app_queue will not ring it, so an
   * agent can sit on Available all morning and take nothing.
   */
  deviceState: string | null;
  /** Asterisk's own per-member counter — resets when app_queue reloads. */
  callsTaken: number;
  lastCallSecondsAgo: number | null;
  queues: QueueView[];
}

function unwrap<T>(res: any): T {
  return (res?.data?.data ?? res?.data) as T;
}

class AgentConsoleService {
  /**
   * The caller's seat, their browser phone, and whether this organisation may
   * dial out. `seat: null` is a normal answer — an admin is not an agent.
   */
  async mySeat(): Promise<{ seat: MySeat | null; hasSoftphone: boolean; softphoneAllowed: boolean; outboundEnabled: boolean }> {
    const b = unwrap<any>(await api.get('/v1/telephony/me/seat'));
    return {
      seat: b?.seat ?? null,
      hasSoftphone: Boolean(b?.hasSoftphone),
      // Purchased seats only (2026-10-14). An older server that does not send
      // the field predates the gate, so absence keeps the old behaviour.
      softphoneAllowed: b?.softphoneAllowed !== false,
      // Explicit true only. An older server that does not send this field
      // leaves the softphone hidden rather than shown — the safe default for a
      // control that dials real numbers.
      outboundEnabled: b?.outboundEnabled === true,
    };
  }

  /**
   * Read live from the switch on every call. Availability is app_queue's state,
   * not a cached intent — a member paused by a supervisor or by an Asterisk
   * restart is paused whatever this app last believed.
   */
  async myState(): Promise<MyState> {
    return unwrap<MyState>(await api.get('/v1/telephony/me/agent-state'));
  }

  /** Applies to every queue: a half-paused agent is not a state to render. */
  async setPaused(paused: boolean, reason?: string): Promise<void> {
    await api.post('/v1/telephony/me/agent-state', reason ? { paused, reason } : { paused });
  }

  /**
   * Code the call that just ended.
   *
   * OPTIONAL: nothing gates on it. A wrap-up an agent cannot skip becomes a
   * stream of codes picked to dismiss the dialog, which counts as data while
   * meaning nothing.
   */
  async fileDisposition(code: string, callerNumber?: string, notes?: string): Promise<void> {
    await api.post('/v1/telephony/me/dispositions', { code, callerNumber, notes });
  }
}

/**
 * Ring my own phone, then connect me to the destination.
 *
 * For anyone with no browser phone. The number rung is resolved server-side from
 * the caller's own profile — there is no parameter for it, so this cannot be used
 * to ring an arbitrary number on the organisation's bill.
 */
export async function clickToCall(destination: string): Promise<{ ringing: string }> {
  const res = await api.post('/v1/telephony/me/click-to-call', { destination });
  return (res?.data?.data ?? res?.data) as { ringing: string };
}

/**
 * The agreed set, as a FALLBACK only.
 *
 * The server owns this list and validates against it, so the two must not
 * diverge — a code the API accepts but the UI never offers is invisible, and one
 * the UI offers but the API rejects is a 400 the agent cannot act on. Prefer
 * {@link fetchDispositionCodes}; this constant exists so a failed request still
 * leaves an agent able to code a call rather than staring at an empty dialog.
 */
export const DISPOSITION_CODES: Array<{ code: string; label: string }> = [
  { code: 'SALE', label: 'Sale' },
  { code: 'NOT_INTERESTED', label: 'Not interested' },
  { code: 'CALLBACK', label: 'Callback' },
  { code: 'WRONG_NUMBER', label: 'Wrong number' },
];

/** The codes the server will actually accept, falling back to the list above. */
export async function fetchDispositionCodes(): Promise<Array<{ code: string; label: string }>> {
  try {
    const res = await api.get('/v1/telephony/me/disposition-codes');
    const d = res?.data?.data ?? res?.data;
    const list = Array.isArray(d) ? d : d?.codes;
    if (Array.isArray(list) && list.length) {
      return list.map((c: any) =>
        typeof c === 'string'
          ? { code: c, label: humanise(c) }
          : { code: c.code, label: c.label || humanise(c.code) });
    }
  } catch { /* fall through — coding a call must not depend on this request */ }
  return DISPOSITION_CODES;
}

/** SALE -> "Sale", NOT_INTERESTED -> "Not interested". */
function humanise(code: string): string {
  const s = String(code).replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const agentConsoleService = new AgentConsoleService();
